import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';
import { StartupProfile as StartupProfileType } from '../types/firestore';
import StartupProfile from '../components/profile/StartupProfile';
import StartupRow from '../components/StartupRow';
import { CATALOG_CATEGORY_LABELS } from '../constants/catalog-categories';
import { FILTERED_GREEN_BUTTON } from '../constants/colors';
import { PRAIRI_SURFACES } from '../constants/prairiSurfaces';

const INVESTOR_PRICING = '/investors/pricing';

type SharedPayload = {
  success?: boolean;
  error?: string;
  share?: {
    id: string;
    startupId: string;
    sharedByName?: string;
    recipientName?: string;
    profile: Partial<StartupProfileType>;
  };
};

const SharedStartupPage: React.FC = () => {
  const { shareId } = useParams<{ shareId: string }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [profile, setProfile] = useState<StartupProfileType | null>(null);
  const [sharedByName, setSharedByName] = useState<string>('');

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!shareId) {
        setError('Shared profile not found');
        setLoading(false);
        return;
      }
      try {
        const getShared = httpsCallable(functions, 'getSharedStartupProfile');
        const result = await getShared({ shareId });
        const data = result.data as SharedPayload;
        if (cancelled) return;
        if (!data?.success || !data.share?.profile) {
          setError(data?.error || 'Shared profile not found');
          setLoading(false);
          return;
        }
        const snap = data.share.profile;
        setSharedByName(data.share.sharedByName || 'An investor');
        setProfile({
          ...(snap as StartupProfileType),
          id: data.share.startupId,
          userId: data.share.startupId,
          companyName: snap.companyName || 'Startup',
          country: snap.country || '',
          slogan: snap.slogan || '',
          pitchSummary: snap.pitchSummary || '',
          url: snap.url || '',
          investment: typeof snap.investment === 'number' ? snap.investment : 0,
          currency: (snap.currency as StartupProfileType['currency']) || 'usd',
          profileCompleted: true,
          currentStep: 0,
          lastCompletedStep: 0,
          createdAt: new Date(),
          updatedAt: new Date(),
          lastUpdatedAt: new Date(),
        } as StartupProfileType);
        setLoading(false);
      } catch {
        if (!cancelled) {
          setError('Failed to load shared profile');
          setLoading(false);
        }
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [shareId]);

  if (loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: PRAIRI_SURFACES.page }}
      >
        <p className="text-white text-lg">Loading shared profile…</p>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div
        className="min-h-screen flex flex-col items-center justify-center gap-4 px-4"
        style={{ backgroundColor: PRAIRI_SURFACES.page }}
      >
        <p className="text-white text-lg">{error || 'Shared profile not found'}</p>
        <Link to={INVESTOR_PRICING} className="text-emerald-400 underline">
          Join PRAIRI as an investor
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: PRAIRI_SURFACES.page }}>
      <div className="flex-1 w-full">
        {/* No side padding — black gutters bleed like dashboard; share line sits below. */}
        <div className="relative z-20 w-full overflow-visible pt-4 md:pt-5 lg:pt-6">
          <StartupProfile profile={profile} hideActions compactDeck flushColumnGutter />
        </div>

        <p className="text-gray-300 text-sm sm:text-base px-3 lg:px-4 pt-2 pb-1">
          <span className="font-semibold text-white">{sharedByName}</span> shared this startup
          profile with you on PRAIRI.
        </p>

        <div className="w-full px-2 sm:px-3 lg:px-4 pt-8 pb-8">
          <StartupRow
            title={CATALOG_CATEGORY_LABELS.speciallyForYou}
            startups={[]}
            onSelectStartup={() => undefined}
            emptyBody={
              <>
                <p className="mb-3 text-xl font-semibold text-white sm:text-2xl">Interested?</p>
                <p className="mb-5 text-base text-white sm:text-lg">
                  Create an investor account to follow startups and message founders.
                </p>
                <Link
                  to={INVESTOR_PRICING}
                  className={`inline-flex items-center justify-center rounded-full px-6 py-2.5 text-base font-medium ${FILTERED_GREEN_BUTTON}`}
                >
                  Create an investor account
                </Link>
              </>
            }
          />
        </div>
      </div>
    </div>
  );
};

export default SharedStartupPage;
